import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDownIcon,
  SearchIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from "lucide-react";

import { ExploreImageBulkDeleteBar } from "@/components/tools/ExploreImageBulkDeleteBar";
import { ExploreImageChip } from "@/components/tools/ExploreImageChip";
import { ExploreImageLightbox } from "@/components/tools/ExploreImageLightbox";
import { ExploreImageTile } from "@/components/tools/ExploreImageTile";
import { ExploreImageUploadDialog } from "@/components/tools/ExploreImageUploadDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useGetExploreImages } from "@/hooks/queries/useGetExploreImages";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSetTitle } from "@/hooks/useSetTitle";
import { positionCategoryToLabel } from "@/lib/constants";
import {
  ExploreImageFilters,
  chamferClipPath,
  exploreImageIndustryToLabel,
  filterExploreImages,
} from "@/lib/exploreImages";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { ExploreImageIndustry, PositionCategory } from "@/types";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const SKELETON_HEIGHTS = [220, 300, 180, 260, 200, 320, 240, 190, 280, 210];

type Toggle = "lowRes" | "portrait" | "noCaption" | "noKeywords";
const TOGGLES: { key: Toggle; label: string }[] = [
  { key: "lowRes", label: "Low res" },
  { key: "portrait", label: "Portrait" },
  { key: "noCaption", label: "No caption" },
  { key: "noKeywords", label: "No keywords" },
];
const NO_TOGGLES: Record<Toggle, boolean> = {
  lowRes: false,
  portrait: false,
  noCaption: false,
  noKeywords: false,
};
const NO_UPLOADS: { files: File[]; index: number } = { files: [], index: 0 };

// Shared by the header's two dropdown triggers and menus.
const DROPDOWN_TRIGGER_CLASS_NAME =
  "flex items-center gap-2 bg-black/[0.05] px-3 py-1.5 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#212327] transition-colors hover:bg-black/[0.09] dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/[0.12]";
const DROPDOWN_CONTENT_CLASS_NAME =
  "rounded-none border-black/10 bg-white text-[#212327] dark:border-white/10 dark:bg-[#1A1B1E] dark:text-[#EAEAEA]";
const DROPDOWN_ITEM_CLASS_NAME =
  "rounded-none font-plex-mono text-[11px] uppercase tracking-[0.12em] focus:bg-black/[0.06] focus:text-[#212327] dark:focus:bg-white/10 dark:focus:text-white";

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && !!target.closest("input, textarea");

// Header collapse: scrolling down by more than this hides everything but
// search, scrolling up by more than this brings it back. Within the top band
// the header is always open.
const SCROLL_DIRECTION_THRESHOLD = 10;
const ALWAYS_EXPANDED_SCROLL_TOP = 24;
// The collapse grows the scroll area, which can clamp scrollTop at the very
// bottom and read as an upward scroll. Ignore scroll events for the length of
// the animation so that can't toggle it straight back.
const COLLAPSE_SETTLE_MS = 260;

// Animates a header section between its natural height and 0 via grid rows;
// `invisible` lands at the end so collapsed controls can't take focus.
const collapsibleClassName = (isCollapsed: boolean) =>
  cn(
    "grid transition-[grid-template-rows,opacity,visibility] duration-200 ease-out motion-reduce:transition-none",
    isCollapsed
      ? "invisible grid-rows-[0fr] opacity-0"
      : "visible grid-rows-[1fr] opacity-100",
  );

export const ToolsExploreImagesPage = () => {
  /* React Hooks */
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<PositionCategory | null>(null);
  const [industry, setIndustry] = useState<ExploreImageIndustry | null>(null);
  const [toggles, setToggles] = useState<Record<Toggle, boolean>>(NO_TOGGLES);
  const [uploads, setUploads] = useState(NO_UPLOADS);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // dragenter/dragleave fire for every child, so count depth to know when
  // the files actually left the page.
  const dragDepthRef = useRef(0);

  /* Database CRUD Operations */
  // Read
  const { data: images, status } = useGetExploreImages();

  const showErrorToast = useErrorToast();

  useSetTitle({ title: "Tools | Explore Images" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.EXPLORE_IMAGES);
  }, []);

  const isUploading = uploads.files.length > 0;

  // "/" jumps to search from anywhere on the page.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "/" || selectedId || isUploading || isTyping(e.target))
        return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedId, isUploading]);

  // Stable, so the memoized tiles only re-render when their own box flips.
  const handleToggleChecked = useCallback((id: string) => {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Collapse the header while scrolling down, expand it on the way back up.
  // Reads scroll position once per frame and only sets state on a change.
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    let isCollapsed = false;
    let lastScrollTop = scrollArea.scrollTop;
    let settleUntil = 0;
    let frame = 0;

    const update = () => {
      frame = 0;
      const scrollTop = scrollArea.scrollTop;
      const now = performance.now();
      if (now < settleUntil) {
        lastScrollTop = scrollTop;
        return;
      }

      let next = isCollapsed;
      if (scrollTop <= ALWAYS_EXPANDED_SCROLL_TOP) {
        next = false;
      } else {
        const delta = scrollTop - lastScrollTop;
        // Small movements accumulate against the last baseline.
        if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD) return;
        next = delta > 0;
      }
      lastScrollTop = scrollTop;
      if (next === isCollapsed) return;

      isCollapsed = next;
      settleUntil = now + COLLAPSE_SETTLE_MS;
      setIsHeaderCollapsed(next);
    };

    const handleScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    scrollArea.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollArea.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  /* Helpers */
  const filters: ExploreImageFilters = {
    search,
    category,
    industry,
    ...toggles,
  };
  const allImages = useMemo(() => images ?? [], [images]);
  const results = useMemo(
    () =>
      filterExploreImages(allImages, {
        search,
        category,
        industry,
        ...toggles,
      }),
    [allImages, search, category, industry, toggles],
  );
  // Only checked images still on screen get deleted; ones a filter hid stay
  // checked for when they come back, but aren't counted or sent.
  const visibleCheckedIds = useMemo(
    () => results.filter((image) => checkedIds.has(image.id)).map((i) => i.id),
    [results, checkedIds],
  );
  // Each chip counts what you'd see if you picked it, given everything else.
  const countWith = (overrides: Partial<ExploreImageFilters>) =>
    filterExploreImages(allImages, { ...filters, ...overrides }).length;
  const hasFilters =
    !!search ||
    !!category ||
    !!industry ||
    Object.values(toggles).some(Boolean);
  // Secondary chips only show while they'd match something, or are on.
  const visibleToggles = TOGGLES.map((toggle) => ({
    ...toggle,
    count: countWith({ [toggle.key]: true }),
  })).filter(({ key, count }) => count > 0 || toggles[key]);

  /* Handlers */
  const handleClearFilters = () => {
    setSearch("");
    setCategory(null);
    setIndustry(null);
    setToggles(NO_TOGGLES);
  };

  // Opens the upload view, which steps through the files one at a time.
  const handleFiles = (files: File[]) => {
    const accepted = files.filter((file) =>
      ACCEPTED_IMAGE_TYPES.includes(file.type),
    );
    if (accepted.length < files.length) {
      showErrorToast({ description: "Only JPEG, PNG, and WebP are accepted." });
    }
    if (accepted.length) setUploads({ files: accepted, index: 0 });
  };

  const handleNextUpload = () => {
    setUploads((current) =>
      current.index + 1 < current.files.length
        ? { ...current, index: current.index + 1 }
        : NO_UPLOADS,
    );
  };

  const handleStopSelecting = () => {
    setIsSelectMode(false);
    setCheckedIds(new Set());
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    dragDepthRef.current++;
    setIsDraggingFiles(true);
  };

  const handleDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (!dragDepthRef.current) setIsDraggingFiles(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (isUploading) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  /* Render */
  const renderGallery = () => {
    if (status === "pending")
      return (
        <div className="columns-[300px] gap-3">
          {SKELETON_HEIGHTS.map((height, i) => (
            <div
              key={i}
              className="mb-3 animate-pulse break-inside-avoid bg-black/[0.06] dark:bg-white/[0.05]"
              style={{ height, clipPath: chamferClipPath(14) }}
            />
          ))}
        </div>
      );
    if (status === "error")
      return (
        <p className="font-plex-mono text-sm uppercase tracking-[0.12em] text-red-600 dark:text-red-400">
          Error loading explore images.
        </p>
      );
    if (!results.length)
      return (
        <div className="flex flex-col items-start gap-4 py-24">
          <span className="font-plex-mono text-[11px] uppercase tracking-[0.2em] text-[#77787A]">
            No matches
          </span>
          <p className="max-w-md text-2xl font-medium text-[#4C4F52] dark:text-[#DDDDDD]">
            {allImages.length
              ? "Nothing in the library matches those filters."
              : "The library is empty. Drop images anywhere to upload them."}
          </p>
          {hasFilters && (
            <ExploreImageChip
              label="Clear filters"
              onClick={handleClearFilters}
              tone="outline"
            />
          )}
        </div>
      );

    return (
      <div className="columns-[300px] gap-3">
        {results.map((image, index) => (
          <ExploreImageTile
            key={image.id}
            image={image}
            index={index}
            isSelectMode={isSelectMode}
            isSelected={checkedIds.has(image.id)}
            onClick={isSelectMode ? handleToggleChecked : setSelectedId}
          />
        ))}
      </div>
    );
  };

  return (
    // The header sits above the scroll area rather than inside it, so when it
    // collapses the scroll area just grows: scrollTop and the grid's place in
    // it are untouched.
    <div
      className="relative flex h-svh flex-col overflow-hidden bg-[#F5F5F5] text-[#212327] dark:bg-[#111214] dark:text-[#FAFAFA]"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={handleDrop}
    >
      <header className="z-20 shrink-0 border-b border-black/10 bg-[#F5F5F5] px-6 dark:border-white/10 dark:bg-[#111214]">
        {/* Title, stats and upload: hidden while scrolling down. */}
        <div
          aria-hidden={isHeaderCollapsed}
          className={collapsibleClassName(isHeaderCollapsed)}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex items-center gap-4 pb-2.5 pt-4">
              <div className="flex items-baseline gap-3">
                <h1 className="text-xl font-semibold leading-none tracking-[-0.01em]">
                  Image library
                </h1>
              </div>
              <span className="font-plex-mono text-[10px] uppercase tracking-[0.2em] text-[#77787A]">
                <span className="text-[#212327] dark:text-white">
                  {allImages.length}
                </span>{" "}
                images ·{" "}
                <span className="text-[#D17949] dark:text-[#F39562]">
                  {results.length}
                </span>{" "}
                shown
              </span>

              <div className="ml-auto flex items-center gap-2">
                <button
                  className={cn(
                    DROPDOWN_TRIGGER_CLASS_NAME,
                    isSelectMode &&
                      "bg-[#212327] text-[#FAFAFA] hover:bg-[#212327]/90 dark:bg-[#FAFAFA] dark:text-[#212327] dark:hover:bg-[#FAFAFA]/90",
                  )}
                  onClick={() => {
                    if (isSelectMode) handleStopSelecting();
                    else setIsSelectMode(true);
                  }}
                  style={{ clipPath: chamferClipPath(6) }}
                  type="button"
                >
                  <Trash2Icon className="h-3.5 w-3.5" />
                  {isSelectMode ? "Cancel bulk delete" : "Bulk delete"}
                </button>
                <button
                  className="flex items-center gap-2 bg-[#D17949] px-4 py-1.5 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#FFF5F0] transition-colors hover:bg-[#F39562] disabled:bg-black/10 disabled:text-black/40 dark:disabled:bg-white/10 dark:disabled:text-white/40"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ clipPath: chamferClipPath(6) }}
                  type="button"
                >
                  <UploadIcon className="h-3.5 w-3.5" />
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Gap above search when the title row is collapsed away. */}
        <div
          className={cn(
            "transition-[height] duration-200 ease-out motion-reduce:transition-none",
            isHeaderCollapsed ? "h-2.5" : "h-0",
          )}
        />

        {/* Search: the only part left when collapsed. */}
        <div className="flex items-center gap-3 rounded-md bg-white px-3 ring-1 ring-inset ring-black/10 transition-shadow focus-within:ring-[#D17949] dark:bg-white/[0.05] dark:ring-white/10 dark:focus-within:ring-[#D17949]">
          <SearchIcon className="h-4 w-4 shrink-0 text-[#77787A]" />
          <input
            className="h-10 min-w-0 flex-1 bg-transparent text-sm text-[#212327] outline-none placeholder:text-[#9E9E9E] dark:text-white dark:placeholder:text-[#77787A]"
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSearch("");
                e.currentTarget.blur();
              }
            }}
            placeholder="Search everything: captions, keywords, categories, industries, image ids"
            ref={searchInputRef}
            value={search}
          />
          {isHeaderCollapsed && hasFilters && (
            <span className="shrink-0 font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#D17949] dark:text-[#F39562]">
              {results.length} {results.length === 1 ? "result" : "results"}
            </span>
          )}
          {search ? (
            <button
              aria-label="Clear search"
              className="text-[#77787A] hover:text-[#212327] dark:hover:text-white"
              onClick={() => {
                setSearch("");
              }}
              type="button"
            >
              <XIcon className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="rounded-sm px-1.5 font-plex-mono text-[11px] text-[#77787A] ring-1 ring-black/15 dark:ring-white/15">
              /
            </kbd>
          )}
        </div>

        {/* Filters: hidden while scrolling down. The bottom padding lives
            here too, so the collapsed bar is just search plus a small gap. */}
        <div
          aria-hidden={isHeaderCollapsed}
          className={collapsibleClassName(isHeaderCollapsed)}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-wrap items-center gap-1.5 pb-3 pt-2.5">
              <ExploreImageChip
                count={countWith({ category: null })}
                label="All"
                onClick={() => {
                  setCategory(null);
                }}
                tone={category ? "default" : "active"}
              />
              {Object.values(PositionCategory).map((value) => (
                <ExploreImageChip
                  key={value}
                  count={countWith({ category: value })}
                  label={positionCategoryToLabel[value]}
                  onClick={() => {
                    setCategory(category === value ? null : value);
                  }}
                  tone={category === value ? "active" : "default"}
                />
              ))}
              <span className="mx-1 h-5 w-px bg-black/10 dark:bg-white/10" />
              {/* 15 industries are too many for chips, so a dropdown. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      DROPDOWN_TRIGGER_CLASS_NAME,
                      industry &&
                        "bg-[#212327] text-[#FAFAFA] hover:bg-[#212327]/90 dark:bg-[#FAFAFA] dark:text-[#212327] dark:hover:bg-[#FAFAFA]/90",
                    )}
                    style={{ clipPath: chamferClipPath(6) }}
                    type="button"
                  >
                    {industry
                      ? exploreImageIndustryToLabel[industry]
                      : "Industry"}
                    <span className="opacity-50">
                      {countWith({ industry: null })}
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className={cn(
                    DROPDOWN_CONTENT_CLASS_NAME,
                    "max-h-[60vh] overflow-y-auto",
                  )}
                >
                  <DropdownMenuItem
                    className={DROPDOWN_ITEM_CLASS_NAME}
                    onSelect={() => {
                      setIndustry(null);
                    }}
                  >
                    <span className="flex-1">All industries</span>
                    <span className="opacity-50">
                      {countWith({ industry: null })}
                    </span>
                  </DropdownMenuItem>
                  {Object.values(ExploreImageIndustry).map((value) => (
                    <DropdownMenuItem
                      key={value}
                      className={cn(
                        DROPDOWN_ITEM_CLASS_NAME,
                        industry === value &&
                          "text-[#D17949] dark:text-[#F39562]",
                      )}
                      onSelect={() => {
                        setIndustry(value);
                      }}
                    >
                      <span className="flex-1">
                        {exploreImageIndustryToLabel[value]}
                      </span>
                      <span className="pl-4 opacity-50">
                        {countWith({ industry: value })}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {visibleToggles.length ? (
                <span className="mx-1 h-5 w-px bg-black/10 dark:bg-white/10" />
              ) : null}
              {visibleToggles.map(({ count, key, label }) => (
                <ExploreImageChip
                  key={key}
                  count={count}
                  label={label}
                  onClick={() => {
                    setToggles((current) => ({
                      ...current,
                      [key]: !current[key],
                    }));
                  }}
                  tone={toggles[key] ? "copper" : "outline"}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Gap under search when the filters are collapsed away. */}
        <div
          className={cn(
            "transition-[height] duration-200 ease-out motion-reduce:transition-none",
            isHeaderCollapsed ? "h-2.5" : "h-0",
          )}
        />
        <input
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          className="hidden"
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            if (files.length) handleFiles(files);
          }}
          ref={fileInputRef}
          type="file"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto" ref={scrollAreaRef}>
        <main className="px-6 py-5">{renderGallery()}</main>

        <footer className="px-6 pb-8 font-plex-mono text-[10px] uppercase tracking-[0.2em] text-[#9E9E9E] dark:text-[#4C4F52]">
          / search · ← → step through · esc close · drop files anywhere to
          upload
        </footer>
      </div>

      {isDraggingFiles && (
        <div className="pointer-events-none fixed inset-0 z-30 flex items-center justify-center bg-[#F5F5F5]/80 backdrop-blur-sm dark:bg-[#111214]/80">
          <div className="flex flex-col items-center gap-3 border-2 border-dashed border-[#D17949] px-16 py-12">
            <UploadIcon className="h-8 w-8 text-[#D17949]" />
            <span className="font-plex-mono text-xs uppercase tracking-[0.24em] text-[#D17949] dark:text-[#F39562]">
              Drop to upload
            </span>
          </div>
        </div>
      )}

      {isSelectMode ? (
        <ExploreImageBulkDeleteBar
          onCancel={handleStopSelecting}
          onDeleted={handleStopSelecting}
          selectedIds={visibleCheckedIds}
        />
      ) : null}

      <ExploreImageLightbox
        images={results}
        onSelect={setSelectedId}
        selectedId={selectedId}
      />

      <ExploreImageUploadDialog
        files={uploads.files}
        index={uploads.index}
        onClose={() => {
          setUploads(NO_UPLOADS);
        }}
        onNext={handleNextUpload}
      />
    </div>
  );
};
